// @givmo/app-admin — employer gift-card Idempotency-Key signature (EP-WEB4). ★ MONEY-MOVING ★
//
// Sending a company gift card CHARGES a company card or DEBITS the company wallet via
// `POST /admin/company/gift-cards`, guarded by the backend `claim_idempotency` dependency — which
// de-dupes ONLY across retries carrying the SAME `Idempotency-Key` + `purchase_request_id`. An UNKEYED
// retry (or a fresh key on retry) mints a new server-side series and does NOT cross-retry-dedup, so a
// double-click / network retry would DOUBLE-CHARGE. A stable client key is the only thing preventing
// that. (Unlike the /give flow, this key rides BOTH the `Idempotency-Key` header AND the body
// `purchase_request_id` — the gift-card wire carries it in both places; see `buildCompanyGiftCardBody`.)
//
// Series identity for a gift-card send = the exact recipient (work email) + the whole-dollar amount +
// the funding source (a specific card id, or the wallet). Changing ANY of these is a genuinely
// different gift → a fresh key (re-mint on edit). The generic lifecycle holder is shared from
// `@givmo/payments`; this file supplies the gift-card-specific signature, a bound factory, and the
// page-owned charge series both send dialogs hold (the key AND the price it was stated at, GC-FU31).

import { IdempotencyKeyHolder } from '@givmo/payments';
import type { GiftCardQuote } from './gift-card-quote';
import type { Funding } from './gift-cards-decode';

/** A stable signature of the gift being sent; a change ends the current attempt-series. */
export interface GiftCardSignature {
  /** The recipient roster employee's work email (the `recipient_work_email` delivery address). */
  readonly recipientWorkEmail: string;
  /** Whole-dollar integer amount. */
  readonly amount: number;
  /** The funding source — a specific card id, or the wallet (encodes the `card_id` axis). */
  readonly funding: Funding;
}

/** Deterministic fingerprint of a gift: recipient + amount + funding instrument (incl. card id). */
export function giftCardFingerprint(signature: GiftCardSignature): string {
  const fundingTag =
    signature.funding.kind === 'wallet' ? 'wallet' : `card:${signature.funding.cardId}`;
  return `email:${signature.recipientWorkEmail}|amount:${signature.amount}|funding:${fundingTag}`;
}

/**
 * Construct the gift-card idempotency-key holder, bound to {@link giftCardFingerprint}. A React
 * component holds ONE holder for its lifetime (a `useRef`), so the key is stable across re-renders by
 * construction and re-mints only when the signature changes. `generate` is injectable for tests. The
 * minted value (a UUIDv4) satisfies the backend `purchase_request_id` min-length-8 constraint.
 */
export function createGiftCardIdempotencyKeyHolder(
  generate?: () => string,
): IdempotencyKeyHolder<GiftCardSignature> {
  return new IdempotencyKeyHolder<GiftCardSignature>(giftCardFingerprint, generate);
}

/**
 * One send request as it went out (GC-FU31): the gift's signature and, on a company card, the server's
 * quote its stated figures (`expected_amount_charged` / `expected_fee`) were taken from — `null` on
 * the wallet, which states none. Frozen when the first attempt of a series starts.
 */
export interface GiftCardHeldRequest<S> {
  readonly signature: S;
  readonly quote: GiftCardQuote | null;
}

/**
 * A send dialog's charge series, PAGE-OWNED (the Give dialog's `GiveSeries`, CAM-R59b): the
 * idempotency key and the request it was minted for, held above the dialog so both survive its unmount
 * on close. A maybe-charged send — "Try again", or the same gift re-entered after a close — is found by
 * its signature and resent with the price it was STATED at, under the same key, so the backend
 * replays its outcome (or books its capture) instead of refusing a body that moved under the key. The
 * stated figures are not part of the signature: a price that moves never mints a second key for a
 * gift that may already be charged. Cleared on a confirmed success and on a definite no-charge.
 */
export class GiftCardChargeSeries<S> {
  readonly #holder: IdempotencyKeyHolder<S>;
  readonly #fingerprint: (signature: S) => string;
  #request: GiftCardHeldRequest<S> | null = null;

  constructor(fingerprint: (signature: S) => string, generate?: () => string) {
    this.#holder = new IdempotencyKeyHolder<S>(fingerprint, generate);
    this.#fingerprint = fingerprint;
  }

  /** The key for this request — minted, or reused for the same signature — held with the request. */
  keyFor(request: GiftCardHeldRequest<S>): string {
    const key = this.#holder.keyFor(request.signature);
    this.#request = request;
    return key;
  }

  /** The request held for this gift, or `null` (none held, or another gift's). */
  heldFor(signature: S): GiftCardHeldRequest<S> | null {
    const held = this.#request;
    return held !== null && this.#fingerprint(held.signature) === this.#fingerprint(signature)
      ? held
      : null;
  }

  /** The current key without minting (a test/inspection seam), or `null`. */
  get current(): string | null {
    return this.#holder.current;
  }

  /** Drop the key and its request: the next send starts a new series. */
  clear(): void {
    this.#holder.clear();
    this.#request = null;
  }
}

/** Construct the single send's page-owned series. `generate` is injectable for tests. */
export function createGiftCardSeries(
  generate?: () => string,
): GiftCardChargeSeries<GiftCardSignature> {
  return new GiftCardChargeSeries<GiftCardSignature>(giftCardFingerprint, generate);
}
